import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  faArrowUpRightFromSquare,
  faChevronDown,
  faCircleMinus,
  faCirclePlus,
  faLocationDotSlash,
  faMap,
  faPen,
  faSearch
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { importLibrary } from '@googlemaps/js-api-loader'
import clsx from 'clsx'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import type { InputInFormProps } from '~/components/Form/Input/input'
import { TextInput } from '~/components/Form/Input/TextInput'
import {
  errorMessage,
  inputError,
  inputLabel,
  inputWrapper
} from '~/components/Form/Input/TextInput.css'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { formatAddress } from '~/domains/common/utils'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import type { AddressResponse } from '~/types/global'
import {
  type Address,
  type AddressSuggestion,
  getGooglePlaceMapId,
  getPlaceDetails,
  useGoogleAddressSearch
} from '~/utils/address'
import { fieldset, fieldsetOpen } from '../Form.css'
import {
  actionButton,
  actionButtonAlways,
  actionButtonVisible,
  addressRoot,
  addressSearchInput,
  autocompleteField,
  autocompleteRow,
  complementToggle,
  localityField,
  localityFieldNarrow,
  localityRow,
  mapContainer,
  mapContainerOpen,
  noResultsMessage,
  searchContainer,
  valueBoxWithActions
} from './AddressInput.css'
import {
  dropDownValueBox,
  openIcon,
  openIconOpen,
  optionItem,
  optionsPortal,
  optionsWrapper,
  wrapper
} from './Dropdown.css'
import {
  valueBox,
  valueBoxFullWidth,
  valueBoxPlaceholder,
  valueBoxText
} from './Input.css'

export type AddressInputProps = InputInFormProps & {
  name?: string
  label?: string
  placeholder?: string
  defaultValue?: AddressResponse | null | ''
  onChange?: (address: Address) => void
  geocode?: boolean
  error?: null | { message: string }
}

const hasCoordinates = (address: Address | null | undefined): boolean =>
  address?.lat != null && address?.lng != null

export const AddressInput = ({
  name,
  label,
  placeholder = 'Sélectionner une adresse',
  defaultValue,
  onChange,
  register,
  setValue,
  error,
  geocode = true
}: AddressInputProps): ReactElement => {
  const { suggestions, isLoading, searchQuery, setSearchQuery } =
    useGoogleAddressSearch()
  const postcodeInputId = `address-postcode-${name ?? 'field'}`

  const [selectedAddress, setSelectedAddress] = useState<Address | null>(() => {
    if (defaultValue && typeof defaultValue === 'object') {
      return defaultValue
    }
    return null
  })

  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isComplementOpen, setIsComplementOpen] = useState(() =>
    Boolean(
      defaultValue && typeof defaultValue === 'object' && defaultValue.line2
    )
  )
  const [isMapOpen, setIsMapOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const labelRef = useRef<HTMLLabelElement | null>(null)
  const mapRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.Marker | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(5),
      flip(),
      shift(),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const click = useClick(context)
  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      if (labelRef.current?.contains(event.target as Node)) {
        return false
      }
      return true
    }
  })
  const role = useRole(context, { role: 'listbox' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role
  ])

  if (register !== undefined && name !== undefined) {
    register(name)
  }

  useEffect(() => {
    if (isMounted && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isMounted])

  useEffect(() => {
    if (!isMapOpen) return

    const onClickOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsMapOpen(false)
      }
    }
    document.addEventListener('click', onClickOutside)
    return () => document.removeEventListener('click', onClickOutside)
  }, [isMapOpen])

  useEffect(() => {
    const mapElement = mapRef.current
    if (
      !isMapOpen ||
      !selectedAddress ||
      !hasCoordinates(selectedAddress) ||
      !mapElement
    ) {
      return
    }

    const lat = selectedAddress.lat
    const lng = selectedAddress.lng
    if (lat == null || lng == null) {
      return
    }

    const mapId = getGooglePlaceMapId()

    const renderMap = async () => {
      await importLibrary('maps')
      const position = { lat, lng }

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapElement, {
          ...(mapId ? { mapId } : {}),
          center: position,
          disableDefaultUI: true,
          zoom: 11
        })
        markerRef.current = new google.maps.Marker({
          position,
          map: mapInstanceRef.current
        })
      } else {
        mapInstanceRef.current.setCenter(position)
        markerRef.current?.setPosition(position)
      }
    }

    void renderMap()
  }, [isMapOpen, selectedAddress])

  const emitChange = (address: Address) => {
    setSelectedAddress(address)
    onChange?.(address)
    if (setValue && name) {
      setValue(name, address, {
        shouldDirty: true,
        shouldValidate: true
      })
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleSuggestionSelect = async (suggestion: AddressSuggestion) => {
    setIsLoadingDetails(true)
    try {
      let next: Address = {
        line1: suggestion.label
      }
      if (geocode) {
        const details = await getPlaceDetails(suggestion.placeId)
        if (details) {
          next = {
            ...details,
            ...(selectedAddress?.id != null ? { id: selectedAddress.id } : {}),
            ...(selectedAddress?.line2 ? { line2: selectedAddress.line2 } : {})
          }
        }
      }

      emitChange(next)
      setIsOpen(false)
      setSearchQuery('')
      setIsMapOpen(false)

      if (!next.postcode) {
        setIsDetailOpen(true)
        setTimeout(() => {
          document.getElementById(postcodeInputId)?.focus()
        }, 50)
      }
    } catch (err) {
      console.error('Error fetching address details:', err)
    } finally {
      setIsLoadingDetails(false)
    }
  }

  // Manual edits invalidate client geocode; backend will re-queue (TODO + clear geocoder)
  const handleManualFieldChange = (field: keyof Address, value: string) => {
    const next: Address = {
      ...selectedAddress,
      [field]: value || undefined,
      lat: null,
      lng: null
    }
    emitChange(next)
    setIsMapOpen(false)
  }

  const googleMapsUrl =
    hasCoordinates(selectedAddress) && selectedAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${selectedAddress.lat},${selectedAddress.lng}`)}`
      : undefined

  const inputEl = (
    <div className={addressRoot} ref={rootRef}>
      <div className={autocompleteRow}>
        <div className={clsx(wrapper, autocompleteField)}>
          <div
            className={clsx(
              dropDownValueBox,
              valueBox,
              valueBoxWithActions,
              selectedAddress === null ? valueBoxPlaceholder : null,
              valueBoxFullWidth,
              error && inputError
            )}
            {...getReferenceProps()}
            ref={refs.setReference}
          >
            <span className={valueBoxText}>
              {selectedAddress === null
                ? placeholder
                : formatAddress(selectedAddress) || placeholder}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={clsx(openIcon, isOpen ? openIconOpen : null)}
            />
          </div>
          <FloatingPortal>
            {isMounted && (
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                className={optionsPortal}
                {...getFloatingProps()}
              >
                <div
                  style={{ ...transitionStyles }}
                  className={clsx(themeClass, optionsWrapper)}
                >
                  <div className={searchContainer}>
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      className={addressSearchInput}
                      placeholder="Rechercher une adresse..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {isLoading || isLoadingDetails ? (
                    <Loader small />
                  ) : suggestions.length > 0 ? (
                    suggestions.map((suggestion) => (
                      <div
                        key={suggestion.placeId}
                        className={optionItem}
                        onClick={() => handleSuggestionSelect(suggestion)}
                      >
                        {suggestion.label}
                      </div>
                    ))
                  ) : (
                    <div className={noResultsMessage}>
                      <FontAwesomeIcon icon={faLocationDotSlash} />
                      {searchQuery
                        ? 'Aucune adresse trouvée'
                        : 'Commencez à taper pour rechercher'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </FloatingPortal>
        </div>

        <button
          type="button"
          className={clsx(
            actionButton,
            hasCoordinates(selectedAddress) && actionButtonVisible
          )}
          title="Afficher la carte"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsMapOpen((open) => !open)
          }}
        >
          <FontAwesomeIcon icon={faMap} />
        </button>
        {googleMapsUrl ? (
          <a
            className={clsx(actionButton, actionButtonVisible)}
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Ouvrir dans Google Maps"
            onClick={(e) => e.stopPropagation()}
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
        ) : null}
        <button
          type="button"
          className={clsx(actionButton, actionButtonAlways)}
          title="Modifier l'adresse"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsDetailOpen((open) => !open)
          }}
        >
          <FontAwesomeIcon icon={faPen} />
        </button>
      </div>

      <div
        ref={mapRef}
        className={clsx(mapContainer, isMapOpen && mapContainerOpen)}
      />

      <div className={clsx(fieldset, isDetailOpen && fieldsetOpen)}>
        <TextInput
          standalone
          label="Adresse"
          value={selectedAddress?.line1 ?? ''}
          onChange={(e) =>
            handleManualFieldChange('line1', e.currentTarget.value)
          }
        />
        <div className={localityRow}>
          <div className={localityFieldNarrow}>
            <TextInput
              standalone
              label="Code postal"
              id={postcodeInputId}
              value={selectedAddress?.postcode ?? ''}
              onChange={(e) =>
                handleManualFieldChange('postcode', e.currentTarget.value)
              }
            />
          </div>
          <div className={localityField}>
            <TextInput
              standalone
              label="Ville"
              value={selectedAddress?.city ?? ''}
              onChange={(e) =>
                handleManualFieldChange('city', e.currentTarget.value)
              }
            />
          </div>
          <div className={localityField}>
            <TextInput
              standalone
              label="Pays"
              value={selectedAddress?.country ?? ''}
              onChange={(e) =>
                handleManualFieldChange('country', e.currentTarget.value)
              }
            />
          </div>
        </div>
        <button
          type="button"
          className={complementToggle}
          onClick={() => setIsComplementOpen((open) => !open)}
        >
          <FontAwesomeIcon
            icon={isComplementOpen ? faCircleMinus : faCirclePlus}
          />
          Complément
        </button>
        {isComplementOpen && (
          <TextInput
            standalone
            value={selectedAddress?.line2 ?? ''}
            onChange={(e) =>
              handleManualFieldChange('line2', e.currentTarget.value)
            }
          />
        )}
      </div>

      {error && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )

  if (!label) {
    return inputEl
  }

  return (
    <div className={inputWrapper}>
      <label
        htmlFor={name}
        className={inputLabel}
        ref={labelRef}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
      >
        {label}
      </label>
      {inputEl}
    </div>
  )
}
